import useAnvaFlowProductsBasketStore, {
  Package,
} from '../store/zustand/flows-products/useAnvaFlowProductsBasketStore';
import { AnvaQuote } from '../types/anva';

/**
 * Determines if a quote should be treated as a maatwerk offerte
 * @param quote The quote to check
 * @returns True if the quote is a maatwerk offerte
 */
export const isMaatwerkQuote = (
  quote: Pick<
    AnvaQuote,
    'isMaatwerk' | 'error' | 'success' | 'isLoading' | 'total_premium'
  >,
): boolean => {
  // Check if explicitly marked as maatwerk
  if (quote.isMaatwerk) return true;

  // Check if there's an error or calculation failed
  if (quote.error || (quote.success === false && !quote.isLoading)) return true;

  // Check if premium is less than €5 per year
  if (quote.total_premium < 5) return true;

  return false;
};

/**
 * Determines if a package should be treated as a maatwerk offerte
 * @param pkg The package to check
 * @returns True if the package is a maatwerk offerte
 */
export const isMaatwerkPackage = (pkg: Package): boolean => {
  // Check if explicitly marked as maatwerk
  if (pkg.isMaatwerk) return true;

  // Check if any quote in the package is maatwerk
  return pkg.quotes.some((quote) => isMaatwerkQuote(quote));
};

/**
 * Calculates the total price of all standalone insurance quotes
 * @returns The sum of total_premium for all insurance quotes
 */
export const getTotalInsurancesPrice = (): Record<string, number> => {
  const { insurances } = useAnvaFlowProductsBasketStore.getState();
  return insurances.reduce((total, quote) => {
    // Skip maatwerk quotes in calculations
    if (isMaatwerkQuote(quote)) return total;

    const interval = quote.payment_interval || 'ANNUAL';
    total[interval] = (total[interval] || 0) + quote.total_premium;
    return total;
  }, {} as Record<string, number>);
};

/**
 * Calculates the total price of all quotes within packages
 * @returns The sum of total_premium for all quotes in packages
 */
export const getTotalPackagesPrice = (): Record<string, number> => {
  const { packages } = useAnvaFlowProductsBasketStore.getState();
  return packages.reduce((total, pkg) => {
    // Skip maatwerk packages in calculations
    if (isMaatwerkPackage(pkg)) return total;

    const packageTotal = pkg.quotes.reduce(
      (subtotal, quote) => subtotal + quote.total_premium,
      0,
    );
    const paymentInterval = pkg.payment_interval || 'ANNUAL';
    total[paymentInterval] = (total[paymentInterval] || 0) + packageTotal || 0;
    return total;
  }, {} as Record<string, number>);
};

/**
 * Checks if the basket contains any maatwerk items
 * @returns True if any item in the basket is maatwerk
 */
export const hasAnyMaatwerkItems = (): boolean => {
  const { packages, insurances } = useAnvaFlowProductsBasketStore.getState();

  // Check if any standalone insurance is maatwerk
  const hasMaatwerkInsurance = insurances.some((quote) =>
    isMaatwerkQuote(quote),
  );

  // Check if any package is maatwerk
  const hasMaatwerkPackage = packages.some((pkg) => isMaatwerkPackage(pkg));

  return hasMaatwerkInsurance || hasMaatwerkPackage;
};

/**
 * Checks if all maatwerk items have been requested
 * @returns True if all maatwerk items have had their requests made
 */
export const areAllMaatwerkItemsRequested = (): boolean => {
  const { packages, insurances } = useAnvaFlowProductsBasketStore.getState();

  // Check standalone insurances
  const maatwerkInsurances = insurances.filter((quote) =>
    isMaatwerkQuote(quote),
  );
  const allInsurancesRequested = maatwerkInsurances.every(
    (quote) => quote.maatwerkRequested,
  );

  // Check packages
  const maatwerkPackages = packages.filter((pkg) => isMaatwerkPackage(pkg));
  const allPackagesRequested = maatwerkPackages.every(
    (pkg) => pkg.maatwerkRequested,
  );

  return allInsurancesRequested && allPackagesRequested;
};

/**
 * Gets only the non-maatwerk packages from the basket
 * @returns Array of packages that are not maatwerk offerte
 */
export const getNonMaatwerkPackages = (): Package[] => {
  const { packages } = useAnvaFlowProductsBasketStore.getState();
  return packages.filter((pkg) => !isMaatwerkPackage(pkg));
};

/**
 * Gets only the non-maatwerk insurances from the basket
 * @returns Array of insurances that are not maatwerk offerte
 */
export const getNonMaatwerkInsurances = (): AnvaQuote[] => {
  const { insurances } = useAnvaFlowProductsBasketStore.getState();
  return insurances.filter((quote) => !isMaatwerkQuote(quote));
};

/**
 * Gets counts of maatwerk vs non-maatwerk items in the basket
 * @returns Object with counts of different item types
 */
export const getBasketItemCounts = (): {
  totalPackages: number;
  totalInsurances: number;
  maatwerkPackages: number;
  maatwerkInsurances: number;
  nonMaatwerkPackages: number;
  nonMaatwerkInsurances: number;
} => {
  const { packages, insurances } = useAnvaFlowProductsBasketStore.getState();

  const maatwerkPackages = packages.filter((pkg) =>
    isMaatwerkPackage(pkg),
  ).length;
  const maatwerkInsurances = insurances.filter((quote) =>
    isMaatwerkQuote(quote),
  ).length;

  return {
    totalPackages: packages.length,
    totalInsurances: insurances.length,
    maatwerkPackages,
    maatwerkInsurances,
    nonMaatwerkPackages: packages.length - maatwerkPackages,
    nonMaatwerkInsurances: insurances.length - maatwerkInsurances,
  };
};

/**
 * Calculate the applicable discount percentage for a package based on the number of quotes
 * @param pkg The package to calculate discount for
 * @returns The applicable discount percentage (e.g., 0.1 for 10%)
 */
export const getApplicableDiscount = (pkg: Package): number => {
  if (
    !pkg.discounts ||
    pkg.discounts.length === 0 ||
    pkg.excluded_from_discounts
  ) {
    return 0;
  }

  // Sort discounts by amount_of_policies in descending order to get the highest applicable discount first
  const sortedDiscounts = [...pkg.discounts].sort(
    (a, b) => b.minimum_amount_of_policies - a.minimum_amount_of_policies,
  );

  // Find the highest discount tier that applies based on quote count
  const quoteCount = pkg.quotes.length;
  const applicableDiscount = sortedDiscounts.find(
    (discount) => quoteCount >= discount.minimum_amount_of_policies,
  );

  // Return the percentage as decimal (e.g., 10% becomes 0.1)
  return applicableDiscount ? applicableDiscount.percentage / 100 : 0;
};

/**
 * Calculates the total discount amount across all packages
 * @returns The sum of discount amounts for all packages
 */
export const getTotalDiscount = (): Record<string, number> => {
  const { packages } = useAnvaFlowProductsBasketStore.getState();
  return packages.reduce((acc, packageItem) => {
    // Calculate raw sum of quotes in this package
    const quoteSum = packageItem.quotes.reduce(
      (subtotal, quote) => subtotal + quote.total_premium,
      0,
    );

    // Calculate discount amount based on package discount percentage
    const discountPercentage = getApplicableDiscount(packageItem);

    // Update cumulative discount amount
    const paymentInterval = packageItem.payment_interval || 'ANNUAL';
    acc[paymentInterval] =
      (acc[paymentInterval] || 0) + quoteSum * discountPercentage;
    return acc;
  }, {} as Record<string, number>);
};

/**
 * Calculates the grand total (insurances + packages - discount)
 * @returns The grand total amount
 */
export const getGrandTotal = (): Record<string, number> => {
  const insurancesPrice = getTotalInsurancesPrice();
  const packagesPrice = getTotalPackagesPrice();
  const discount = getTotalDiscount();

  // Combine all keys from the three records
  const allKeys = [
    ...new Set([
      ...Object.keys(insurancesPrice),
      ...Object.keys(packagesPrice),
      ...Object.keys(discount),
    ]),
  ];

  // Calculate grand total for each payment interval
  return allKeys.reduce((result, key) => {
    result[key] =
      (insurancesPrice[key] || 0) +
      (packagesPrice[key] || 0) -
      (discount[key] || 0);
    return result;
  }, {} as Record<string, number>);
};
